import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { publicationDataAtom } from "../components/analytics/LoadPublicationData";
import { getPercentageOfVisitors } from "../utils/services/linearUserFlowService";
import UserFlowGraph from "../components/analytics/UserFlowGraph";
import {
	getEvents,
	publicationEventsAtom,
} from "../utils/hooks/PublicationHook";
import { getPublicationEventsById } from "../utils/services/publicationEvents";
import { benchmarkCriteriaAtom, publicationIdAtom } from "./Analytics";
import { BenchmarkData, Publication } from "../utils/types/dataTypes";
import { HUNDRED, ONE_DECIMAL, UNDEFINED, ZERO } from "../App";
import { Box, Flex } from "@chakra-ui/react";
import EventCard from "../components/analytics/EventCard";
import TooltipsTemplate from "../utils/tooltips/TooltipsTemplate";
import { ColorVariantEnum, HeadingSizeEnum, Text, theme } from "@foleon/atomic";
import VisitorsOverTimeGraph from "../components/analytics/VisitorsOverTimeGraph";
import ShowBenchmark from "../components/analytics/ShowBenchmark";
import {
	Analytics,
	getDocumentAnalytics,
	Goal,
} from "../services/documentService";
import TargetsContainer from "../components/targets/TargetsContainer";
import TargetsDescription from "../components/targets/TargetDescription";

export const AMOUNT_OF_CRITERIA = 4;

export const calculateBenchmarkForPublication = (
	publication: Publication,
	benchmarkCriteria: BenchmarkData
): any => {
	const uniqueVisitors =
		(publication.data.uniqueVisitors /
			benchmarkCriteria.avgUniqueVisitors) *
		HUNDRED;

	const docVisits =
		(publication.data.docVisits / benchmarkCriteria.avgDocVisits) * HUNDRED;

	const pagePercentage =
		(publication.data.averagePageViewPerSession /
			publication.data.totalPages) *
		HUNDRED;
	const pagesPerVisit =
		(pagePercentage / benchmarkCriteria.avgPagesPerVisit) * HUNDRED;

	const visitTime =
		(publication.data.averageSessionDuration /
			benchmarkCriteria.avgVisitTime) *
		HUNDRED;

	const benchmark =
		Math.round(
			((uniqueVisitors + docVisits + pagesPerVisit + visitTime) /
				AMOUNT_OF_CRITERIA) *
				ONE_DECIMAL
		) / ONE_DECIMAL;
	return benchmark;
};

export const MAX_GOALS = 3;

/**
 * Shows graphs for analytics
 *
 * @constructor
 */
export const AnalyticsDashboard = () => {
	const [publicationData] = useAtom(publicationDataAtom);
	const [publicationId] = useAtom(publicationIdAtom);
	const [isUserFlowGraphLoaded, setUserFlowGraphLoaded] = useState(false);
	const [benchmark, setBenchmark] = useState<number>(UNDEFINED);
	const [isBenchmarkLoaded, setBenchmarkLoaded] = useState(false);
	const [benchmarkCriteria] = useAtom(benchmarkCriteriaAtom);
	const [publicationEvents, setPublicationEvents] = useAtom(
		publicationEventsAtom
	);
	const tooltipContextUserFlow = `This line-graph shows the linear user flow of a Foleon doc.
		It gives you a page-per-page overview of how your Foleon doc is being viewed on an average.
		The Y-axis shows the percentage of total visitors that visited the page.  
		The X-axis shows the page number.`;
	// eslint-disable-next-line max-len
	const tooltipContextVisitors = `This line-graph shows the amount of visitors over time of a Foleon doc in a week.
		It shows you how many visitors there were on a specific day in the week for this doc.
		The Y-axis shows the amount ov visitors on the document.
		The X-axis shows the date of the day.`;
	const [isPublicationEventsLoaded, setPublicationEventsLoaded] =
		useState(false);
	const [isLineGraphLoaded, setLineGraphLoaded] = useState(false);
	const [goals, setGoals] = useState<Goal[]>([]);
	const [analytics, setAnalytics] = useState<Analytics>({
		pagesPerSession: ZERO,
		sessionTime: ZERO,
		uniqueVisitors: ZERO,
		visitorsOverTime: [],
		pages: [],
		totalDocVisits: ZERO,
	});

	useEffect(() => {
		getDocumentAnalytics(String(publicationId)).then((res) => {
			const data = res.data.goals;
			console.log(data);
			setGoals(data.goals);
			setAnalytics({
				uniqueVisitors: data.uniqueVisitors,
				pagesPerSession: data.pagesPerSession,
				sessionTime: data.sessionTime,
				visitorsOverTime: data.visitorsOverTime,
				pages: data.pages,
				totalDocVisits: data.totalDocVisits,
			});

			setLineGraphLoaded(true);
			setUserFlowGraphLoaded(true);
		});

		getPublicationEventsById(publicationId).then((res) => {
			setPublicationEvents(res.data);
			setPublicationEventsLoaded(true);
		});
	}, []);

	useEffect(() => {
		if (publicationData && benchmarkCriteria) {
			setBenchmark(
				calculateBenchmarkForPublication(
					publicationData,
					benchmarkCriteria
				)
			);
			setBenchmarkLoaded(true);
		}
	}, [benchmarkCriteria, publicationData]);

	// only to print benchmark
	useEffect(() => {
		if (isBenchmarkLoaded) console.log("BENCHMARK: " + benchmark);
	}, [isBenchmarkLoaded]);

	return (
		<>
			<Flex
				w="100%"
				direction="column"
				alignItems="center"
				as="nav"
				height="62px"
				bg={theme.colors.primary.blue}
			>
				<Flex display="inline-block" padding="5px 10px">
					<Text.Heading
						size={HeadingSizeEnum.EXTRA_LARGE}
						colorVariant={ColorVariantEnum.WHITE}
					>
						Analytics
					</Text.Heading>
				</Flex>
			</Flex>

			<Flex
				m="0 auto"
				direction="column"
				w="75%"
				gap="32px"
				alignItems="center"
			>
				<TargetsDescription></TargetsDescription>
				{/*<Text.Title><ShowBenchmark benchmark={benchmark}/></Text.Title>*/}
				{goals && goals.length === MAX_GOALS && (
					<TargetsContainer goals={goals} analytics={analytics} />
				)}

				<ShowBenchmark benchmark={benchmark} />
				<Flex
					direction="column"
					borderRadius=".5%"
					alignItems="center"
					marginTop="1%"
					w="100%"
					h="100%"
					shadow="lg"
				>
					<Flex
						marginTop="1%"
						bgGradient="linear-gradient(153deg, rgba(34,54,89,1) 0%,
						  rgba(0,177,255,1) 35%,
						  rgba(0,161,255,1) 64%)"
						m="0"
						w="100%"
						padding="1% 0"
						borderRadius=".5%"
						direction="column"
						alignItems="center"
					>
						<Text.Heading
							size={HeadingSizeEnum.MEDIUM}
							colorVariant={ColorVariantEnum.WHITE}
							margin="0"
						>
							User Flow
						</Text.Heading>
					</Flex>
					<Flex marginTop=".5%">
						<TooltipsTemplate
							text={tooltipContextUserFlow}
						></TooltipsTemplate>
					</Flex>
					{isUserFlowGraphLoaded && (
						<UserFlowGraph
							labels={analytics.pages.map((p, index) =>
								String(index)
							)}
							datasets={[
								{
									label: "Linear User Flow",
									backgroundColor: ["#00B1FF"],
									borderColor: "#0098FF",
									borderWidth: 3,
									data: analytics.pages.map((p) =>
										getPercentageOfVisitors(
											p.uniquePageViewCount,
											analytics.totalDocVisits
										)
									),
								},
							]}
						/>
					)}
				</Flex>
				<Flex
					direction="column"
					borderRadius=".5%"
					alignItems="center"
					w="100%"
					h="100%"
					shadow="lg"
				>
					<Flex
						bgGradient="linear-gradient(153deg, rgba(4,66,4,1) 4%,
									rgba(0,174,0,1) 28%, rgba(98,190,98,1) 89%);"
						w="100%"
						padding="1% 0"
						borderRadius=".5%"
						direction="column"
						alignItems="center"
					>
						<Text.Heading
							size={HeadingSizeEnum.MEDIUM}
							colorVariant={ColorVariantEnum.WHITE}
							margin="0"
						>
							Visitors over time
						</Text.Heading>
					</Flex>
					<Flex marginTop=".5%">
						<TooltipsTemplate
							text={tooltipContextVisitors}
						></TooltipsTemplate>
					</Flex>
					{isLineGraphLoaded && (
						<VisitorsOverTimeGraph
							labels={analytics.visitorsOverTime.map(
								(v) => v.day
							)}
							datasets={[
								{
									label: "Visitors over time",
									data: analytics.visitorsOverTime.map(
										(v) => v.amountOfVisitors
									),
									backgroundColor: ["#00ae00"],
									borderColor: "#009500",
									borderWidth: 3,
								},
							]}
						/>
					)}
				</Flex>
				{(isPublicationEventsLoaded && publicationEvents && (
					<Flex w="100%" gap="4px">
						<Flex
							marginTop="1%"
							bgGradient="linear-gradient(153deg, rgba(120,0,181,1) 0%,
							  rgba(175,71,227,1) 48%,
							  rgba(146,0,220,1) 81%)"
							m="0 auto"
							w="100%"
							padding="16px 0"
							borderRadius="2.5%"
							justifyContent="center"
						>
							<Text.Heading
								size={HeadingSizeEnum.MEDIUM}
								colorVariant={ColorVariantEnum.WHITE}
								margin="0"
							>
								Events
							</Text.Heading>
						</Flex>
						<Flex w="100%" gap="4px" justifyContent="space-between">
							<Box shadow="md">
								<EventCard
									eventName="Hotspots"
									clickAmount={getEvents(
										"hotspots",
										publicationEvents
									)}
									arrowPositive={false}
								/>
							</Box>
							<Box shadow="md">
								<EventCard
									eventName={"Buttons"}
									clickAmount={getEvents(
										"buttons",
										publicationEvents
									)}
								/>
							</Box>
							<Box shadow="md">
								<EventCard
									eventName={"Videos"}
									clickAmount={getEvents(
										"videos",
										publicationEvents
									)}
								/>
							</Box>
							<Box shadow="md">
								<EventCard
									eventName={"Overlay"}
									clickAmount={getEvents(
										"overlays",
										publicationEvents
									)}
									arrowPositive={false}
								/>
							</Box>
							<Box shadow="md">
								<EventCard
									eventName={"Text Links"}
									clickAmount={getEvents(
										"links",
										publicationEvents
									)}
								/>
							</Box>
						</Flex>
					</Flex>
				)) || (
					<Flex w="100%">
						<Flex
							bgGradient="linear-gradient(153deg, rgba(120,0,181,1) 0%,
	  									rgba(175,71,227,1) 48%,
	  									rgba(146,0,220,1) 81%)"
							w="180px"
							padding="32px 0"
							borderRadius="2.5%"
							justifyContent="center"
						>
							<Text.Heading
								size={HeadingSizeEnum.MEDIUM}
								colorVariant={ColorVariantEnum.WHITE}
							>
								Events
							</Text.Heading>
						</Flex>
						<Flex m="auto">
							<Text.Heading size={HeadingSizeEnum.EXTRA_LARGE}>
								Oops no event data!
							</Text.Heading>
						</Flex>
					</Flex>
				)}
			</Flex>
		</>
	);
};

export default AnalyticsDashboard;
